import { ReadonlyVec2, vec3 } from 'gl-matrix';
import { PlaneView } from '../../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';

import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { View } from '~/engine/view/View';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ModifierKeys } from '~/engine/tool/ModifierKeys';
import { MOUSE_BUTTONS_LEFT_MASK } from '~/engine/tool/MouseButton';
import { ContourOperations } from '~/engine/util/algorithms/sdf/ContourOperations';
import { DrawActionState } from '../DrawAction';
import { LinePreviewRenderer, LinePreviewStyle } from './LinePreviewRenderer';

export class Line {
    /** The maximum distance in pixels between the first point and cursor
     *  to snap the line closed when clicking point-by-point. */
    private static readonly CLICK_SNAP_TO_CLOSE_TOLERANCE_PX = 15;
    /** The maximum distance in pixels between the first point and cursor
     * to snap the line closed when drag-drawing. */
    private static readonly DRAG_SNAP_TO_CLOSE_TOLERANCE_PX = 10;

    private _pointsMm: vec3[];
    private _hoverPointMm: vec3 | null;
    private readonly _epsilonToAddPointMm = 0.1;
    private minLineLength = 10;
    private _undoMarkers: number[] = [];

    constructor(startPoint: vec3) {
        this._pointsMm = [startPoint];
        this._hoverPointMm = null;
    }

    public get pointsMm(): readonly vec3[] {
        return this._pointsMm;
    }

    public get hoverPointMm(): vec3 | null {
        return this._hoverPointMm;
    }

    /** Get the maximum distance in pixels between the first point and cursor
     *  to snap the line closed when clicking point-by-point. */
    public static get clickSnapToCloseTolerancePx(): number {
        return Line.CLICK_SNAP_TO_CLOSE_TOLERANCE_PX;
    }

    /** Get the maximum distance in pixels between the first point and cursor
     *  to snap the line closed when drag-drawing. */
    public static get dragSnapToCloseTolerancePx(): number {
        return Line.DRAG_SNAP_TO_CLOSE_TOLERANCE_PX;
    }

    public hover(ptMm: vec3 | null) {
        this._hoverPointMm = ptMm;
    }

    public isOkToAdd(): boolean {
        return true;
    }

    public isClosingPoint(point: vec3, closeEpsilonMm: number): boolean {
        if (!this._hoverPointMm || this._pointsMm.length < 3) {
            return false;
        }

        const pointsClose = this.arePointsClose(
            point,
            this._pointsMm[0],
            closeEpsilonMm
        );

        const lineLength = this.calculateTotalLength();
        const lengthSufficient = lineLength >= this.minLineLength;

        return pointsClose && lengthSufficient;
    }

    /** Record the current point count so that undoLastSegment
     *  can revert back to this position. Call before addPoint
     *  at the start of each new user action (mouse press). */
    public pushUndoMarker(): void {
        this._undoMarkers.push(this._pointsMm.length);
    }

    /** Remove all points added since the last undo marker.
     *  Skips stale markers whose position matches the current
     *  length (e.g. if addPoint rejected the point). Returns
     *  false when no markers remain (only the starting point
     *  is left). */
    public undoLastSegment(): boolean {
        while (this._undoMarkers.length > 0) {
            const marker = this._undoMarkers.pop()!;
            if (marker < this._pointsMm.length) {
                this._pointsMm.length = marker;
                return true;
            }
        }
        return false;
    }

    public addPoint(point: vec3, closeEpsilonMm: number): boolean {
        const lineReady = this.isClosingPoint(point, closeEpsilonMm);
        if (lineReady) {
            this._hoverPointMm = null;
            return true;
        }

        if (this._pointsMm.length > 0) {
            const lastPoint = this._pointsMm[this._pointsMm.length - 1];
            if (
                this.arePointsClose(point, lastPoint, this._epsilonToAddPointMm)
            ) {
                return false;
            }
        }
        this._pointsMm.push(point);
        this._hoverPointMm = null;
        return false;
    }

    private arePointsClose(
        point1: vec3,
        point2: vec3,
        epsilonMm: number
    ): boolean {
        const dx = point1[0] - point2[0];
        const dy = point1[1] - point2[1];
        const dz = point1[2] - point2[2];
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
        return distance < epsilonMm;
    }

    private calculateTotalLength(): number {
        let length = 0;
        for (let i = 1; i < this._pointsMm.length; i++) {
            const p1 = this._pointsMm[i - 1];
            const p2 = this._pointsMm[i];
            const dx = p1[0] - p2[0];
            const dy = p1[1] - p2[1];
            length += Math.sqrt(dx * dx + dy * dy);
        }
        return length;
    }
}

export class LineDrawActionState extends DrawActionState {
    private line: Line | null = null;
    protected readonly _planeView: PlaneView;
    private readonly renderer: LinePreviewRenderer;

    /** True after onMousePress adds a point. When the next
     *  onMouseMove adds a drag point, a second undo marker is
     *  pushed so the straight-line click and the free-form drag
     *  become separate undo steps. */
    private _needsDragMarker = false;
    private _isMouseHeld = false;

    public set mouseHeld(value: boolean) {
        this._isMouseHeld = value;
    }

    private _isShiftHeld = false;
    private readonly _handleKeyEvent = (e: KeyboardEvent) => {
        const shiftNow = e.shiftKey;
        if (shiftNow !== this._isShiftHeld) {
            this._isShiftHeld = shiftNow;
            this.renderer.render(this.line, this.getPreviewStyle());
        }
    };

    public constructor(
        view: PlaneView,
        target: RegionOfInterest,
        targetImage: ImageObject,
        isErasing: boolean
    ) {
        super(view, target, targetImage, isErasing);
        this._planeView = view;

        this.renderer = new LinePreviewRenderer(view, target);

        // Add individual listeners here outside of ToolManager to bypass
        // its multiple places where event propagation is stopped. This way
        // we get modifier keys (shift) here
        document.addEventListener('keydown', this._handleKeyEvent);
        document.addEventListener('keyup', this._handleKeyEvent);
    }

    public dispose(): void {
        document.removeEventListener('keydown', this._handleKeyEvent);
        document.removeEventListener('keyup', this._handleKeyEvent);
        this.renderer.dispose();
    }

    private getCloseEpsilonMm(tolerancePx: number): number {
        const res = this._planeView.resolution;
        return tolerancePx * Math.max(res[0], res[1]);
    }

    private getPreviewStyle(): LinePreviewStyle {
        if (
            this.line &&
            this.line.hoverPointMm !== null &&
            !this._isShiftHeld
        ) {
            const tolerancePx = this._isMouseHeld
                ? Line.dragSnapToCloseTolerancePx
                : Line.clickSnapToCloseTolerancePx;
            const closeEpsilonMm = this.getCloseEpsilonMm(tolerancePx);
            if (
                this.line.isClosingPoint(this.line.hoverPointMm, closeEpsilonMm)
            ) {
                return LinePreviewStyle.CLOSING;
            }
        }
        return LinePreviewStyle.NORMAL;
    }

    public get drawing(): boolean {
        return this.line !== null;
    }

    public get actionName(): string {
        return `Line ${this._erase ? 'erase' : 'draw'}`;
    }

    protected get contourTexture(): ContourTexture | undefined {
        if (!this.line) {
            return undefined;
        }
        return new ContourOperations().polygonToSdf(
            this.line.pointsMm,
            this.targetImage
        );
    }

    public onMousePress(
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        _view: View
    ): EventHandlerResult {
        const mousePoint =
            this._planeView.clientToPatientCoordinates(coordinates);

        if (!this.line) {
            this.line = new Line(mousePoint);
            this._needsDragMarker = true;
        } else {
            this.line.pushUndoMarker();
            const epsilon = modifierKeys.hasShift
                ? 0
                : this.getCloseEpsilonMm(Line.clickSnapToCloseTolerancePx);
            const lineReady = this.line.addPoint(mousePoint, epsilon);
            this._needsDragMarker = true;
            if (lineReady) {
                this.finishDrawing();
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_HANDLED,
                    true,
                    new VolumeViewUpdateEvent(
                        this._targetStructure.boundingBox,
                        LayerBit.CONTOUR_LAYER_BIT,
                        true
                    )
                );
            }
        }
        this.renderer.render(this.line, this.getPreviewStyle());
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        _view: View
    ): EventHandlerResult {
        if (this.line) {
            const mousePoint =
                this._planeView.clientToPatientCoordinates(coordinates);
            this.line.hover(mousePoint);

            if (buttons === MOUSE_BUTTONS_LEFT_MASK && this.line.isOkToAdd()) {
                if (this._needsDragMarker) {
                    this.line.pushUndoMarker();
                    this._needsDragMarker = false;
                }
                this.line.addPoint(mousePoint, 0);
                this.line.hover(mousePoint);
            }
            this.renderer.render(this.line, this.getPreviewStyle());
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseRelease(
        coordinates: ReadonlyVec2,
        _view?: View
    ): EventHandlerResult {
        if (this.line && this.line.pointsMm.length >= 3) {
            const mousePoint =
                this._planeView.clientToPatientCoordinates(coordinates);
            this.line.hover(mousePoint);
            const epsilon = this._isShiftHeld
                ? 0
                : this.getCloseEpsilonMm(Line.dragSnapToCloseTolerancePx);
            if (this.line.isClosingPoint(mousePoint, epsilon)) {
                this.finishDrawing();
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_HANDLED,
                    true,
                    new VolumeViewUpdateEvent(
                        this._targetStructure.boundingBox,
                        LayerBit.CONTOUR_LAYER_BIT,
                        true
                    )
                );
            }
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public finishOnPoint(coordinates: ReadonlyVec2): EventHandlerResult {
        if (this.line) {
            const mousePoint =
                this._planeView.clientToPatientCoordinates(coordinates);
            this.line.addPoint(mousePoint, 0);
            this.finishDrawing();
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true,
                new VolumeViewUpdateEvent(
                    this._targetStructure.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    private finishDrawing(): void {
        this.applyBoolean();
        this.discardDrawing();
    }

    public clearMemory(): void {
        this.renderer.clear();
    }

    public undoLastPoint(): boolean {
        if (!this.line) {
            return false;
        }
        if (!this.line.undoLastSegment()) {
            return false;
        }
        this.renderer.render(this.line, this.getPreviewStyle());
        return true;
    }

    public discardDrawing(): void {
        this.renderer.clear();
        this.line = null;
    }

    public stopAction(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
